/**
 * EVENTSFLO-INTEGRITY-BASELINE-NOISE-REDUCTION-AND-LEGACY-DATA-REMOVAL-V1
 *
 * Read-only operator page for GET /api/admin/integrity/summary.
 * Answers ONE question: "Can I add projects now?"
 *
 * Main view (always visible):
 *   1. Big status verdict.
 *   2. Three headline counts (Blocking / Review-required / Warnings).
 *   3. Short explanation.
 *   4. Four operator-focused domain rows:
 *        - Project & quote calculation status
 *        - File & document status
 *        - Test artefact status
 *        - System protection status
 *
 * Detail / raw JSON is available behind Details toggles. Strictly read-only —
 * no mutation calls.
 */
import { useCallback, useEffect, useState } from "react";
import {
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  ClipboardList,
  Files,
  Beaker,
  Lock,
} from "lucide-react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";

// ── Verdict / status meta ─────────────────────────────────────────────────
const OPERATOR_STATUS_META = {
  OK_TO_ADD_PROJECTS: {
    label: "Safe to add projects",
    explanation: "No blocking or review-required items found.",
    tone: "bg-emerald-500/10 text-emerald-800 border-emerald-500/40",
    dot: "bg-emerald-500",
  },
  WARNINGS_PRESENT_REVIEW_FIRST: {
    label: "Review warnings first",
    explanation:
      "Nothing is blocking. Some items should be reviewed before treating downstream workflows as authoritative.",
    tone: "bg-amber-500/10 text-amber-900 border-amber-500/40",
    dot: "bg-amber-500",
  },
  BLOCKED_FIX_BEFORE_ADDING_PROJECTS: {
    label: "Blocked — fix before adding projects",
    explanation:
      "One or more sections are blocking. Resolve those before entering new real projects.",
    tone: "bg-rose-500/10 text-rose-800 border-rose-500/40",
    dot: "bg-rose-500",
  },
};

const BUCKET_META = {
  OK: { tone: "bg-emerald-500/10 text-emerald-700", label: "OK" },
  WARNING: { tone: "bg-amber-500/10 text-amber-800", label: "Warning" },
  REVIEW_REQUIRED: {
    tone: "bg-orange-500/10 text-orange-700",
    label: "Review required",
  },
  BLOCKING: { tone: "bg-rose-500/10 text-rose-700", label: "Blocking" },
};

// ── Domain grouping: 4 operator-focused rows ──────────────────────────────
// Each domain rolls up 1..N sections from the raw payload.
const DOMAINS = [
  {
    key: "projects_quotes",
    label: "Project & quote calculation status",
    icon: ClipboardList,
    sections: [
      "calculation_drift",
      "quote_snapshot_drift",
      "qty_quantity_divergence",
      "invoice_snapshot_drift",
    ],
    describe: (raw) => {
      const cd = raw?.sections?.calculation_drift;
      const qd = raw?.sections?.quote_snapshot_drift;
      const qq = raw?.sections?.qty_quantity_divergence;
      const parts = [];
      if (cd) {
        parts.push(
          `${cd.shifts_with_drift ?? 0} shift(s) drifted / ${cd.checked_shifts ?? 0} checked`,
        );
      }
      if (qd) {
        parts.push(
          `${qd.quotes_with_drift ?? 0} draft quote(s) drifted / ${qd.checked_quotes ?? 0} checked`,
        );
      }
      if (qq) {
        parts.push(
          `${qq.review_required ?? 0} qty/quantity divergent · ${qq.safe_repair_available ?? 0} auto-repairable / ${qq.total_lines_scanned ?? 0} scanned`,
        );
      }
      return parts.join(" · ");
    },
  },
  {
    key: "files_documents",
    label: "File & document status",
    icon: Files,
    sections: ["logo_hygiene", "crew_document_integrity"],
    describe: (raw) => {
      const cdi = raw?.sections?.crew_document_integrity;
      const lh = raw?.sections?.logo_hygiene;
      const parts = [];
      if (cdi) {
        parts.push(
          `${cdi.current ?? 0} current doc(s) · ${cdi.current_missing_disk_file ?? 0} missing disk file · ${cdi.orphan_disk_files ?? 0} orphan file(s)`,
        );
      }
      if (lh) {
        parts.push(lh.has_logo ? "Logo present" : "No logo configured");
      }
      return parts.join(" · ");
    },
  },
  {
    key: "test_artefacts",
    label: "Test artefact status",
    icon: Beaker,
    sections: ["test_artefacts"],
    describe: (raw) => {
      const t = raw?.sections?.test_artefacts;
      if (!t) return "";
      return `${t.test_docs_on_real_crew ?? 0} TEST doc(s) on real crew · ${t.test_named_projects ?? 0} TEST project(s) · ${t.test_named_clients ?? 0} TEST client(s) · ${t.sandbox_staff_rows ?? 0} sandbox staff row(s)`;
    },
  },
  {
    key: "system_protection",
    label: "System protection status",
    icon: Lock,
    sections: ["sandbox_guards"],
    describe: () =>
      "Logo, crew docs, profile photos and sandbox endpoints ENFORCED.",
  },
];

// Roll up a set of section severities into the worst-case domain severity.
const SEV_ORDER = ["OK", "WARNING", "REVIEW_REQUIRED", "BLOCKING"];
function rollupSeverity(sectionKeys, sections) {
  let worst = "OK";
  for (const k of sectionKeys) {
    const s = sections?.[k]?.severity;
    if (!s) continue;
    if (SEV_ORDER.indexOf(s) > SEV_ORDER.indexOf(worst)) worst = s;
  }
  return worst;
}

function SeverityChip({ severity }) {
  const meta = BUCKET_META[severity] || {
    tone: "bg-slate-500/10 text-slate-700",
    label: severity || "—",
  };
  return (
    <span
      data-testid={`severity-chip-${severity}`}
      className={`inline-flex items-center gap-1 rounded-full border border-current/10 px-2.5 py-0.5 text-xs font-medium ${meta.tone}`}
    >
      {meta.label}
    </span>
  );
}

function DomainRow({ domain, raw }) {
  const [open, setOpen] = useState(false);
  const Icon = domain.icon;
  const severity = rollupSeverity(domain.sections, raw.sections);
  const desc = domain.describe(raw);
  return (
    <Card
      data-testid={`integrity-domain-${domain.key}`}
      className="overflow-hidden"
    >
      <button
        type="button"
        data-testid={`integrity-domain-toggle-${domain.key}`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 p-4"
      >
        <div className="flex items-center gap-3">
          {open ? (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400" />
          )}
          <Icon className="h-5 w-5 text-slate-500" />
          <div className="text-left">
            <div className="text-sm font-medium text-slate-800">
              {domain.label}
            </div>
            {desc && (
              <div className="text-xs text-slate-500">{desc}</div>
            )}
          </div>
        </div>
        <SeverityChip severity={severity} />
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-100 bg-slate-50/50 px-4 py-3">
          {domain.sections.map((k) => (
            <div
              key={k}
              data-testid={`integrity-section-detail-${k}`}
              className="rounded-md bg-white p-3 shadow-sm"
            >
              <div className="mb-1 flex items-center justify-between gap-2">
                <div className="font-mono text-xs uppercase tracking-wide text-slate-500">
                  {k}
                </div>
                <SeverityChip severity={raw.sections?.[k]?.severity} />
              </div>
              <pre className="overflow-x-auto text-xs text-slate-700">
                {JSON.stringify(raw.sections?.[k] ?? {}, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function IntegrityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/integrity/summary");
      setData(res.data);
      setLastFetchedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const statusMeta = data
    ? OPERATOR_STATUS_META[data.operator_status] || null
    : null;

  return (
    <div className="p-6" data-testid="integrity-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-slate-700" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">Integrity</h1>
            <p className="text-sm text-slate-500">
              Operator gate — is the platform safe to add real projects right now?
            </p>
          </div>
        </div>
        <Button
          data-testid="integrity-refresh-button"
          onClick={load}
          disabled={loading}
          variant="outline"
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh Integrity Check
        </Button>
      </div>

      {loading && !data && (
        <div data-testid="integrity-loading" className="text-slate-500">
          Loading integrity summary…
        </div>
      )}

      {error && (
        <Card
          data-testid="integrity-error"
          className="mb-4 border-rose-500/40 bg-rose-500/10 p-4 text-rose-700"
        >
          Could not load integrity summary: {String(error)}
        </Card>
      )}

      {!loading && !error && !data && (
        <div data-testid="integrity-empty" className="text-slate-500">
          No integrity data returned by the server.
        </div>
      )}

      {data && (
        <>
          {/* Verdict + explanation */}
          {statusMeta && (
            <Card
              data-testid="integrity-operator-status"
              className={`mb-6 border p-5 ${statusMeta.tone}`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`mt-1 inline-block h-3 w-3 rounded-full ${statusMeta.dot}`}
                />
                <div className="flex-1">
                  <div className="text-lg font-semibold">
                    {statusMeta.label}
                  </div>
                  <div
                    className="mt-1 text-sm opacity-90"
                    data-testid="integrity-operator-status-explanation"
                  >
                    {statusMeta.explanation}
                  </div>
                  <div className="mt-1 text-xs opacity-60">
                    Raw: {data.operator_status}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Headline counts — Blocking, Review-required, Warnings */}
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Card
              data-testid="integrity-headline-blocking"
              className="p-4"
            >
              <div className="text-xs uppercase tracking-wide text-rose-600">
                Blocking issues
              </div>
              <div className="mt-1 text-3xl font-semibold text-slate-800">
                {data.buckets?.BLOCKING ?? 0}
              </div>
              <div className="text-xs text-slate-500">
                Must be fixed before adding projects.
              </div>
            </Card>
            <Card
              data-testid="integrity-headline-review"
              className="p-4"
            >
              <div className="text-xs uppercase tracking-wide text-orange-600">
                Review-required
              </div>
              <div className="mt-1 text-3xl font-semibold text-slate-800">
                {data.buckets?.REVIEW_REQUIRED ?? 0}
              </div>
              <div className="text-xs text-slate-500">
                Not blocking. Review before treating downstream as authoritative.
              </div>
            </Card>
            <Card
              data-testid="integrity-headline-warning"
              className="p-4"
            >
              <div className="text-xs uppercase tracking-wide text-amber-600">
                Warnings
              </div>
              <div className="mt-1 text-3xl font-semibold text-slate-800">
                {data.buckets?.WARNING ?? 0}
              </div>
              <div className="text-xs text-slate-500">
                Non-critical hygiene items.
              </div>
            </Card>
          </div>

          {/* Four domain rows */}
          <div className="space-y-3">
            {DOMAINS.map((d) => (
              <DomainRow key={d.key} domain={d} raw={data} />
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <div>
              Endpoint generated at: {data.generated_at || "—"}
            </div>
            <div>
              Last fetched: {lastFetchedAt ? lastFetchedAt.toLocaleString() : "—"}
            </div>
          </div>

          {/* Details / raw JSON */}
          <Card className="mt-6 p-4">
            <button
              type="button"
              data-testid="integrity-raw-toggle"
              onClick={() => setRawOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium text-slate-700"
            >
              {rawOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Details / Raw JSON
            </button>
            {rawOpen && (
              <pre
                data-testid="integrity-raw-json"
                className="mt-3 overflow-x-auto rounded-md bg-slate-50 p-3 text-xs text-slate-700"
              >
                {JSON.stringify(data, null, 2)}
              </pre>
            )}
          </Card>

          {/* OK/WARNING/REVIEW/BLOCKING breakdown — kept for parity but small */}
          <div
            className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4"
            data-testid="integrity-buckets"
          >
            {["OK", "WARNING", "REVIEW_REQUIRED", "BLOCKING"].map((k) => (
              <Card
                key={k}
                data-testid={`integrity-bucket-${k}`}
                className="p-3"
              >
                <div className="text-[10px] uppercase tracking-wide text-slate-500">
                  {BUCKET_META[k].label}
                </div>
                <div className="mt-1 text-lg font-semibold text-slate-800">
                  {data.buckets?.[k] ?? 0}
                </div>
              </Card>
            ))}
          </div>

          {data.note && (
            <p
              data-testid="integrity-note"
              className="mt-4 text-xs italic text-slate-500"
            >
              {data.note}
            </p>
          )}
        </>
      )}
    </div>
  );
}
